import * as React from 'react';
import { League, Team } from '../../../../sdk/model';
import { Box, Paper, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, 
    TextField, IconButton, Avatar, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import { teamApi } from '../../../../adapters/APIExporter'
import CloseIcon from '@mui/icons-material/Close';
import SearchUser from '../../../util/SearchUser';

interface allTeamsSettingsProps {
  league: League
}

function AllTeamsSettings(props: allTeamsSettingsProps) {
  const [mutableTeams, setMutableTeams] = React.useState<Team[]>(props.league.teams.map(team => { return {...team}}));
  const [removingTeams, setRemovingTeams] = React.useState(false);
  const [confirmSave, setConfirmSave] = React.useState(false);
  const [errors, setErrors] = React.useState<string[]>([]);
  
  function handleChange(event, teamIndex) {
    const {name, value} = event.target;
    const newMutableTeams = mutableTeams.map(team => {return {...team}});
    const changingTeam = newMutableTeams[teamIndex];
    changingTeam[name] = value;
    setMutableTeams(newMutableTeams);
  }
  
  function selectNewOwner(newOwner, teamIndex) {
    const newMutableTeams = mutableTeams.map(team => {return {...team, owners: [...team.owners]}});
    const changingTeam = newMutableTeams[teamIndex];
    changingTeam.owners.push(newOwner);
    setMutableTeams(newMutableTeams);
  }
  
  function removeOwner(teamIndex, ownerIndex) {
    const newMutableTeams = mutableTeams.map(team => {return {...team, owners: [...team.owners]}});
    const changingTeam = newMutableTeams[teamIndex];
    changingTeam.owners.splice(ownerIndex, 1);
    setMutableTeams(newMutableTeams);
  }
  
  function removeTeam(teamIndex) {
    const newMutableTeams = mutableTeams.map(team => {return {...team}});
    newMutableTeams.splice(teamIndex, 1);
    setMutableTeams(newMutableTeams);
    setRemovingTeams(true);
  }
  
  function addTeam() {
    const newMutableTeams = mutableTeams.map(team => {return {...team}});
    const newTeam = {name: 'Team ' + (newMutableTeams.length + 1), owners: []};
    newMutableTeams.push(newTeam);
    setMutableTeams(newMutableTeams);
  }
  
  function reset() {
    setMutableTeams(props.league.teams.map(team => { return {...team}}));
    setRemovingTeams(false);
    setConfirmSave(false);
  }
  
  function save() {
    if (removingTeams) {
      setConfirmSave(true)
    } else {
      completeSave()
    }
  }
  
  function completeSave() {
    teamApi.updateLeagueTeams(props.league.id, mutableTeams)
      .then(() => {
        setConfirmSave(false);
        setErrors([]);
      }).catch((error) => {
        let errors = [error.response.data.message];
        setErrors(errors);
      });
  }
  
  let errorInfo = errors.map((error, index) => {
    return (<p className='error' key={'error_' + index}>{error}</p>);
  });
  
  return <Box>
    {errorInfo}
    <Dialog onClose={() => setConfirmSave(false)} open={confirmSave}>
      <DialogTitle>Remove Teams?</DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to remove teams from this league?  All active player contracts on these teams will be waived for other teams to claim on the waiver wire.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button color="primary" variant="contained" onClick={() => setConfirmSave(false)}>
          Cancel
        </Button>
        <Button color="secondary" variant="contained" onClick={completeSave}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
    <TableContainer className="centered" component={Paper} sx={{width: '75%'}}><Table>
      <TableHead><TableRow>
        <TableCell>Team Name</TableCell>
        <TableCell>Owner(s)</TableCell>
        <TableCell>Actions</TableCell>
      </TableRow></TableHead>
      <TableBody>
        {mutableTeams.map((team, teamIndex) => <TableRow key={team.id}>
          <TableCell>
            <TextField name='name' label="Team Name" value={team.name}
                onChange={event => handleChange(event, teamIndex)} />
          </TableCell>
          <TableCell>
            {team?.owners?.length ? 
              team!.owners!.map((owner, ownerIndex) => {
                return <Box>
                  <IconButton sx={{ p: 0 }}>
                    <Avatar alt={owner?.username} src={owner?.picture} />
                  </IconButton>
                  {' ' + owner.username}
                  <Button onClick={() => removeOwner(teamIndex, ownerIndex)}>
                    <CloseIcon />
                  </Button>
                  <br/>
                </Box>
              }) : 'No Owner'
            }
            <SearchUser omittedUsers={props.league.teams.flatMap(team => team.owners!).map(user => user.id!)} selectUser={newOwner => selectNewOwner(newOwner, teamIndex)} />
          </TableCell>
          <TableCell>
            <Button className='capsized-button' onClick={() => removeTeam(teamIndex)}>Remove Team</Button>
          </TableCell>
        </TableRow>)}
      </TableBody>
    </Table></TableContainer>
    <br/>
    <Button className='capsized-button' onClick={addTeam}>Add Team</Button>
    <br/>
    <Button className='capsized-button' onClick={reset}>Reset/Undo Changes</Button>
    <Button className='capsized-button' onClick={save}>Save</Button>
  </Box>
}

export default AllTeamsSettings;
